import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { handleErrorMessage } from "../../../../helpers/methods";
import {
  getRelativeFontSize,
  mediumFont,
  regularFont,
} from "../../../../utils/styles";
import { getKarmicResourcesCustomData } from "../../AnalysisService";
import ShadbalaTable from "../Shadbala/ShadbalaTable";

interface CustomProps {
  profileId: string;
  setIsLoading: Function;
}

const ASTRO_COLUMN_KEYS = [
  { key: "house", tKey: "house" },
  { key: "nakshatra", tKey: "nakshatra" },
  { key: "sign", tKey: "sign" },
  { key: "d9_house", tKey: "d9House" },
  { key: "d9_nakshatra", tKey: "d9Nakshatra" },
  { key: "d9_sign", tKey: "d9Sign" },
  { key: "d10_house", tKey: "d10House" },
  { key: "d10_nakshatra", tKey: "d10Nakshatra" },
  { key: "d10_sign", tKey: "d10Sign" },
  { key: "atma_karaka", tKey: "atmaKaraka" },
  { key: "dara_karaka", tKey: "daraKaraka" },
];

const tableStyles = {
  table: {
    borderCollapse: "collapse" as const,
    width: "100%",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "8px",
    overflow: "hidden",
  },
  headerCell: {
    ...mediumFont,
    fontSize: getRelativeFontSize(1),
    color: "#FFFFFF",
    backgroundColor: "rgba(144, 83, 246, 0.4)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "10px 12px",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
  },
  entityCell: {
    ...mediumFont,
    fontSize: getRelativeFontSize(1),
    color: "#FFFFFF",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "8px 12px",
    backgroundColor: "rgba(255, 255, 255, 0.06)",
    textTransform: "capitalize" as const,
    whiteSpace: "nowrap" as const,
  },
  dataCell: {
    ...regularFont,
    fontSize: getRelativeFontSize(1),
    color: "#FFFFFF",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "8px 12px",
    textAlign: "center" as const,
    backgroundColor: "rgba(255, 255, 255, 0.04)",
  },
  karmicDataCell: {
    ...regularFont,
    fontSize: getRelativeFontSize(1),
    color: "#FFFFFF",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "8px 12px",
    backgroundColor: "rgba(255, 255, 255, 0.04)",
  },
};

const sectionTitle = {
  ...mediumFont,
  fontSize: getRelativeFontSize(3),
  color: "#FFFFFF",
};

const KarmicResourcesCustom = (props: CustomProps) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<Record<string, any> | undefined>();

  useEffect(() => {
    if (props.profileId) {
      fetchKarmicResourcesCustomData();
    }
  }, [i18n.language]);

  const fetchKarmicResourcesCustomData = async () => {
    try {
      props.setIsLoading(true);
      const requestData = {
        profile_id: props.profileId,
        language: i18n.language.toLowerCase(),
      };
      const response = await getKarmicResourcesCustomData(requestData);
      setData(response);
      props.setIsLoading(false);
    } catch (error: any) {
      props.setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const renderKarmicTable = (tableData: any[]) => {
    if (!tableData || tableData.length === 0) return null;

    return (
      <Box sx={{ overflowX: "auto", py: 2 }}>
        <Table sx={{ ...tableStyles.table, minWidth: 600 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableStyles.headerCell}>{t("number")}</TableCell>
              <TableCell
                sx={{
                  ...tableStyles.headerCell,
                }}
              >
                {t("karmicResourcesKetu")}
              </TableCell>
              <TableCell
                sx={{
                  ...tableStyles.headerCell,
                }}
              >
                {t("source")}
              </TableCell>
              <TableCell
                sx={{
                  ...tableStyles.headerCell,
                }}
              >
                {t("lifeGoalsRahu")}
              </TableCell>
              <TableCell
                sx={{
                  ...tableStyles.headerCell,
                }}
              >
                {t("source")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row: any) => (
              <TableRow key={row.number}>
                <TableCell sx={tableStyles.karmicDataCell}>
                  {row.is_primary ? `${row.number}*` : row.number}
                </TableCell>
                <TableCell
                  sx={{
                    ...tableStyles.karmicDataCell,
                    color: "#4CAF50",
                    textDecoration: row.is_primary ? "underline" : undefined,
                  }}
                >
                  {row.resource}
                </TableCell>
                <TableCell
                  sx={{
                    ...tableStyles.karmicDataCell,
                    color: "#4CAF50",
                  }}
                >
                  {row.resource_source}
                </TableCell>
                <TableCell
                  sx={{
                    ...tableStyles.karmicDataCell,
                    color: "#F44336",
                    textDecoration: row.is_primary ? "underline" : undefined,
                  }}
                >
                  {row.goal}
                </TableCell>
                <TableCell
                  sx={{
                    ...tableStyles.karmicDataCell,
                    color: "#F44336",
                  }}
                >
                  {row.goal_source}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  const renderEntityTable = (astroData: Record<string, any>) => {
    const entities = Object.keys(astroData).filter((k) => k !== "shadbala");
    if (entities.length === 0) return null;

    const activeColumns = ASTRO_COLUMN_KEYS.filter((col) =>
      entities.some(
        (entity) =>
          astroData[entity][col.key] != null &&
          astroData[entity][col.key] !== "",
      ),
    );

    return (
      <Box sx={{ overflowX: "auto", py: 2 }}>
        <Table sx={{ ...tableStyles.table, minWidth: 480 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableStyles.headerCell}>{t("entity")}</TableCell>
              {activeColumns.map((col) => (
                <TableCell key={col.key} sx={tableStyles.headerCell}>
                  {t(col.tKey)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {entities.map((entity) => (
              <TableRow key={entity}>
                <TableCell sx={tableStyles.entityCell}>
                  {entity.replace(/_/g, " ")}
                </TableCell>
                {activeColumns.map((col) => (
                  <TableCell key={col.key} sx={tableStyles.dataCell}>
                    {astroData[entity][col.key] != null
                      ? String(astroData[entity][col.key])
                      : "—"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Stack direction={"column"} spacing={3} py={2}>
      {data?.table && data.table.length > 0 && (
        <Box>{renderKarmicTable(data.table)}</Box>
      )}

      {data?.image_base64 && (
        <Box>
          <Typography sx={sectionTitle}>{t("chart")}</Typography>
          <Box
            component="img"
            src={`data:image/png;base64,${data.image_base64}`}
            alt={t("karmicResourcesChart")}
            sx={{
              my: 2,
              width: "-webkit-fill-available",
              maxWidth: "100%",
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          />
        </Box>
      )}

      {data?.astro_data && (
        <Box>
          <Typography sx={sectionTitle}>{t("astroData")}</Typography>
          {renderEntityTable(data.astro_data)}
        </Box>
      )}

      {data?.astro_data?.shadbala && (
        <Box>
          <Typography sx={sectionTitle}>{t("shadbala")}</Typography>
          <ShadbalaTable shadbalaDetails={data.astro_data.shadbala} />
        </Box>
      )}
    </Stack>
  );
};

export default KarmicResourcesCustom;
